import { useEffect, useState } from "react";
import { Field, Input, Select } from "./ui";
import { sizesFor, categoryHasSizes, OTHER_SIZE } from "../lib/categories";

/* Size input that follows the chosen category.

   Shows nothing for categories without meaningful sizing, a dropdown where
   there is a standard scale, and a free-text box when the seller picks
   "Other" — or when they're editing an older listing whose free-text size
   predates the dropdowns, which must not silently lose its value. */
export function SizeField({ category, value, onChange, label = "Size", hint = "" }) {
  const options = sizesFor(category);
  const hasOptions = categoryHasSizes(category);

  // An existing value that isn't in the list means either a legacy free-text
  // size or a deliberate "Other" — either way, keep showing it as text.
  const [custom, setCustom] = useState(
    () => Boolean(value) && hasOptions && !options.includes(value)
  );

  useEffect(() => {
    // When the category changes, a size from the old scale is meaningless
    // (a "YM" jersey size on a ball). Clear it rather than submit nonsense.
    if (!hasOptions) {
      if (value) onChange("");
      setCustom(false);
      return;
    }
    if (value && !options.includes(value) && !custom) onChange("");
    // Intentionally keyed on `category` only — this should fire when the
    // category changes, not on every keystroke in the custom-size box.
  }, [category]); // eslint-disable-line

  if (!hasOptions) return null;

  return (
    <Field label={label} hint={hint}>
      {custom ? (
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter a size"
            autoFocus
          />
          <button
            type="button"
            className="shrink-0 text-sm font-medium text-brand-600 hover:text-brand-700"
            onClick={() => {
              setCustom(false);
              onChange("");
            }}
          >
            Use list
          </button>
        </div>
      ) : (
        <Select
          value={value || ""}
          onChange={(e) => {
            if (e.target.value === OTHER_SIZE) {
              setCustom(true);
              onChange("");
            } else {
              onChange(e.target.value);
            }
          }}
        >
          <option value="">Any / not specified</option>
          {options.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          <option value={OTHER_SIZE}>Other…</option>
        </Select>
      )}
    </Field>
  );
}
